import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type {
  MintReferralCodeRequest,
  RedeemReferralCodeRequest,
  ReferralAdminOverview,
  ReferralCode,
  ReferralRedemption,
  ReferralStats,
} from './types.js';

/**
 * Org self-service referrals. Any org can mint codes and redeem one (subject
 * to server-side guards: never-attributed, not reseller-associated, and
 * pre-first-purchase). Two-sided credits fire once the referred org's
 * cumulative spend reaches the snapshotted threshold.
 */
export class ReferralsClient {
  constructor(private readonly http: BaseClient) {}

  /** Mint a new referral code for the caller's org. */
  async mintCode(body: MintReferralCodeRequest = {}): Promise<ReferralCode> {
    const res = await this.http.post<{ success: boolean; data: ReferralCode }>('/referrals/codes', { body });
    return unwrapData(res);
  }

  /** List the caller org's codes with per-code redeemed/qualified counts. */
  async listCodes(): Promise<ReferralCode[]> {
    const res = await this.http.get<{ success: boolean; data: ReferralCode[] }>('/referrals/codes');
    return res.data ?? [];
  }

  /** Activate or deactivate one of the caller org's codes. */
  async setCodeActive(codeId: string, isActive: boolean): Promise<ReferralCode> {
    const res = await this.http.patch<{ success: boolean; data: ReferralCode }>(
      `/referrals/codes/${codeId}`,
      { body: { isActive } },
    );
    return unwrapData(res);
  }

  /** Caller org's referral summary as referrer (redemptions, qualified, credits earned). */
  async stats(): Promise<ReferralStats> {
    const res = await this.http.get<{ success: boolean; data: ReferralStats }>('/referrals/stats');
    return unwrapData(res);
  }

  /**
   * Redeem a referral code for the caller's org. Fails (4xx with an
   * `error_code`) when a guard rejects it: `code_inactive`, `self_referral`,
   * `already_redeemed`, `already_associated`, or `has_purchases`.
   */
  async redeem(body: RedeemReferralCodeRequest): Promise<ReferralRedemption> {
    const res = await this.http.post<{ success: boolean; data: ReferralRedemption }>(
      '/referrals/redeem',
      { body },
    );
    return unwrapData(res);
  }

  // --- Platform-admin only ---

  /**
   * Platform-wide referral monitor: rollup stats + every redemption (with the
   * code string and both org names). Returns 403 unless platform admin.
   */
  async adminOverview(): Promise<ReferralAdminOverview> {
    const res = await this.http.get<{ success: boolean; data: ReferralAdminOverview }>('/admin/referrals');
    return unwrapData(res);
  }

  /**
   * Void a PENDING redemption (abuse control) so it can never qualify. Fails
   * with 409 `not_pending` if the redemption is missing, already qualified, or
   * already void. Platform-admin only.
   */
  async voidRedemption(redemptionId: string): Promise<void> {
    await this.http.post<{ success: boolean }>(`/admin/referrals/${redemptionId}/void`, { body: {} });
  }

  /**
   * Force-qualify a PENDING non-reseller redemption: grants the snapshotted
   * referrer reward + referred welcome credit immediately, bypassing the spend
   * threshold. Fails with 409 `not_pending` (missing/qualified/void) or 400
   * `reseller_code`. Platform-admin only. Returns the updated redemption.
   */
  async qualifyRedemption(redemptionId: string): Promise<ReferralRedemption> {
    const res = await this.http.post<{ success: boolean; data: ReferralRedemption }>(
      `/admin/referrals/${redemptionId}/qualify`,
      { body: {} },
    );
    return unwrapData(res);
  }
}
